// src/Pages/conges/ChefDashboard.jsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { API_URL } from '../Config';
import { Users, Calendar, Clock, AlertCircle, CheckCircle, RefreshCw, Eye, FileText } from 'lucide-react';

const ChefDashboard = () => {
  const [conges, setConges] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCongesAPendingConfirmation();
  }, []);

  const fetchCongesAPendingConfirmation = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/chef/conges`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        }
      });
      
      if (!response.ok) throw new Error('Erreur chargement');
      
      const data = await response.json();
      setConges(data.data || data || []);
    } catch (error) {
      toast.error('Erreur lors du chargement');
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen  flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin mx-auto"></div>
          <p className="mt-3 text-gray-500 text-sm">Chargement...</p>
        </div>
      </div>
    );
  }

  const pendingConges = conges.filter(c => c.status === 'valide_par_validateur');
  const processedConges = conges.filter(c => c.status !== 'valide_par_validateur');
  
  const urgentConges = pendingConges.filter(c => {
    const today = new Date();
    const startDate = new Date(c.date_debut);
    const diffTime = startDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays <= 2 && diffDays >= 0;
  });

  const longConges = conges.filter(c => c.duree >= 7);

  return (
    <div className="min-h-screen py-8">
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">Confirmation des congés</h1>
              <p className="text-gray-500 text-sm mt-1">Validez les demandes pré-approuvées</p>
            </div>
            <button
              onClick={fetchCongesAPendingConfirmation}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md"
              title="Actualiser"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <div className="bg-white border border-gray-200 rounded p-4">
              <div className="text-lg font-semibold text-gray-900">{conges.length}</div>
              <div className="text-sm text-gray-500">Total</div>
            </div>
            <div className="bg-white border border-yellow-100 rounded p-4 bg-yellow-50">
              <div className="text-lg font-semibold text-yellow-700">{pendingConges.length}</div>
              <div className="text-sm text-yellow-600">À traiter</div>
            </div>
            <div className="bg-white border border-red-100 rounded p-4 bg-red-50">
              <div className="text-lg font-semibold text-red-700">{urgentConges.length}</div>
              <div className="text-sm text-red-600">Urgent</div>
            </div>
            <div className="bg-white border border-gray-200 rounded p-4">
              <div className="text-lg font-semibold text-gray-900">{longConges.length}</div>
              <div className="text-sm text-gray-500">Longue durée</div>
            </div>
          </div>
        </div>

        {/* Content */}
        {conges.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-lg p-8 text-center">
            <CheckCircle className="w-12 h-12 text-green-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Aucune demande</h3>
            <p className="text-gray-500 text-sm">
              Toutes les demandes ont été traitées
            </p>
          </div>
        ) : (
          <>
            {/* Pending Section */}
            {pendingConges.length > 0 && (
              <div className="mb-6">
                <div className="flex items-center text-sm text-gray-500 mb-3">
                  <AlertCircle className="w-4 h-4 mr-2 text-yellow-500" />
                  Demandes à traiter ({pendingConges.length})
                </div>
                
                <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="min-w-full">
                      <thead className="">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Employé</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Période</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Durée</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {pendingConges.map((conge) => {
                          const isUrgent = urgentConges.includes(conge);
                          
                          return (
                            <tr key={conge.id} className={`hover: ${isUrgent ? null : ''}`}>
                              <td className="px-6 py-4">
                                <div className="flex items-center">
                                  <Users className="w-4 h-4 text-gray-400 mr-2" />
                                  <span className="text-sm font-medium text-gray-900">
                                    {conge.user?.nom || '-'}
                                  </span>
                                </div>
                              </td>
                              <td className="px-6 py-4">
                                <div className="flex items-center text-sm text-gray-900">
                                  <Calendar className="w-4 h-4 text-gray-400 mr-2" />
                                  {formatDate(conge.date_debut)} - {formatDate(conge.date_fin)}
                                </div>
                              </td>
                              <td className="px-6 py-4">
                                <div className="flex items-center text-sm text-gray-900">
                                  <Clock className="w-4 h-4 text-gray-400 mr-2" />
                                  {conge.duree} jour{conge.duree > 1 ? 's' : ''}
                                </div>
                              </td>
                              <td className="px-6 py-4">
                                <span className={`inline-flex px-2.5 py-0.5 text-xs rounded-full ${
                                  conge.type === 'exceptionnel' 
                                    ? 'bg-purple-100 text-purple-700' 
                                    : 'bg-gray-100 text-gray-700'
                                }`}>
                                  {conge.type === 'exceptionnel' ? 'Exceptionnel' : 'Normal'}
                                </span>
                              </td>
                              <td className="px-6 py-4">
                                <div className="flex space-x-2">
                                  <Link
                                    to={`/chef/conge/${conge.id}/confirmer`}
                                    className="px-3 py-1.5 text-sm bg-green-600 text-white rounded hover:bg-green-700"
                                  >
                                    Traiter
                                  </Link>
                                  <Link
                                    to={`/conge/${conge.id}`}
                                    className="px-3 py-1.5 text-sm border border-gray-300 text-gray-700 rounded hover:"
                                  >
                                    <Eye className="w-3 h-3 inline mr-1" />
                                    Voir
                                  </Link>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* Processed Section */}
            {processedConges.length > 0 && (
              <div>
                <div className="flex items-center text-sm text-gray-500 mb-3">
                  <CheckCircle className="w-4 h-4 mr-2 text-green-500" />
                  Demandes traitées ({processedConges.length})
                </div>
                
                <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="min-w-full">
                      <thead className="">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Employé</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Période</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {processedConges.map((conge) => (
                          <tr key={conge.id} className="hover:">
                            <td className="px-6 py-4">
                              <div className="text-sm font-medium text-gray-900">
                                {conge.user?.nom || '-'}
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="text-sm text-gray-900">
                                {formatDate(conge.date_debut)} - {formatDate(conge.date_fin)}
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <span className={`inline-flex px-2.5 py-0.5 text-xs rounded-full ${
                                conge.status === 'confirme_par_chef'
                                  ? 'bg-green-100 text-green-700'
                                  : 'bg-red-100 text-red-700'
                              }`}>
                                {conge.status === 'confirme_par_chef' ? 'Confirmé' : 'Refusé'}
                              </span>
                            </td>
                            <td className="px-6 py-4">
                              <Link
                                to={`/conge/${conge.id}`}
                                className="px-3 py-1.5 text-sm border border-gray-300 text-gray-700 rounded hover:"
                              >
                                <Eye className="w-3 h-3 inline mr-1" />
                                Voir
                              </Link>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* Footer */}
            <div className="mt-6 px-6 py-3  border border-gray-200 rounded-lg">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                <div className="text-sm text-gray-600 mb-2 sm:mb-0">
                  {conges.length} demande{conges.length !== 1 ? 's' : ''} au total
                </div>
                <div className="text-xs text-gray-500">
                  {pendingConges.length} en attente de traitement
                </div>
              </div>
            </div>
          </>
        )}

        {/* Informations */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white border border-gray-200 rounded-lg p-5">
            <h3 className="text-sm font-medium text-gray-900 mb-3">Instructions</h3>
            <ul className="space-y-2">
              <li className="flex items-start text-sm text-gray-600">
                <div className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-1.5 mr-3"></div>
                <span>Vérifiez l'impact sur l'activité de l'équipe</span>
              </li>
              <li className="flex items-start text-sm text-gray-600">
                <div className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-1.5 mr-3"></div>
                <span>Confirmez uniquement les congés pré-validés par les RH</span>
              </li>
              <li className="flex items-start text-sm text-gray-600">
                <div className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-1.5 mr-3"></div>
                <span>Fournissez une justification pour chaque décision</span>
              </li>
            </ul>
          </div>
          
          <div className="bg-white border border-gray-200 rounded-lg p-5">
            <h3 className="text-sm font-medium text-gray-900 mb-3">Priorités</h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Début sous 48h</span>
                <span className="text-sm font-medium text-red-600">{urgentConges.length}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Longue durée (7+ jours)</span>
                <span className="text-sm font-medium text-orange-600">{longConges.length}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Exceptionnels</span>
                <span className="text-sm font-medium text-purple-600">
                  {conges.filter(c => c.type === 'exceptionnel').length}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChefDashboard;